
/**
 * Cross-tab logout coordination.
 * Problem: the access token lives in sessionStorage, which is per-tab. When one tab
 * logs out, the other tabs keep their in-memory user and keep working.
 * Solution: the logging-out tab writes a *timestamp* into localStorage:
 *   - stamp older  -> someone logged out BEFORE we logged in: ignore it.
 *   - stamp newer  -> someone logged out AFTER we logged in: we must log out too.
 *  we use a single flag and single timestamp variable for the variable, so no overloading.
 */
export const LOGOUT_FLAG_KEY = 'logoutAt';

/** Stamp "this tab just logged out" so the other tabs notice. */
export function markLogout() {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LOGOUT_FLAG_KEY, String(Date.now()));
}

/** True when another tab logged out after `sessionStartedAt` (ms since epoch). */
export function isLoggedOutByOtherTab(sessionStartedAt) {
  if (typeof window === 'undefined') return false;

  const stamp = Number(localStorage.getItem(LOGOUT_FLAG_KEY) || 0);
  return !!sessionStartedAt && stamp > sessionStartedAt;
}

/** Key used to remember where the user wanted to go before being sent to the IdP. */
export const POST_LOGIN_REDIRECT_KEY = 'post_login_redirect_url';

/** Remember the current location so we can return to it after the IdP redirect. */
export function rememberPostLoginRedirect(pathname) {
  if (typeof window === 'undefined') return;

  const target = `${pathname || '/'}${window.location.search || ''}`;
  if (target && target !== '/') {
    localStorage.setItem(POST_LOGIN_REDIRECT_KEY, target);
  }
}

/** Read (and clear) the remembered location, so it is used only once. */
export function consumePostLoginRedirect() {
  if (typeof window === 'undefined') return undefined;

  const target = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  if (target) localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return target || undefined;
}

/**
 * Put the browser back on the application root after the IdP redirected us to
 * /authentication/callback?code=...&state=...
 *
 * Two things matter here:
 *  1. The whole PATH must be removed, not just the query string. If
 *     /authentication/callback stays in the URL, React Router matches the
 *     catch-all route and shows "Oops! Page not found".
 *  2. `history.replaceState` is a raw History API call and does NOT notify React
 *     Router. Without dispatching `popstate`, the router keeps believing it is
 *     still on the callback path, so route matching never runs again.
 *
 * @param {string} [destination] Optional path to land on (e.g. the route the user
 *   originally requested). Defaults to the app root when omitted.
 */
export function stripCallbackFromUrl(destination) {
  if (typeof window === 'undefined') return;

  if (window.location.pathname.includes('/authentication/callback')) {
    const baseUrl = document.getElementsByTagName('base')[0]?.getAttribute('href');
    window.history.replaceState({}, document.title, destination || baseUrl || '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

export default stripCallbackFromUrl;
